import { UserClaims } from '../model/userClaims';

export const ANALYTICS_REDACTED_PLACEHOLDER = '[REDACTED]';

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function extractUsernameFromUname(uname: string): string | null {
    const trimmed = uname.trim();
    if (!trimmed) {
        return null;
    }

    const segments = trimmed.split(/\\+/);
    const username = segments[segments.length - 1]?.trim();
    return username || null;
}

function addTerm(terms: Set<string>, term: string | null | undefined): void {
    const trimmed = term?.trim();
    if (trimmed) {
        terms.add(trimmed);
    }
}

function addFirstAndLastNameTerms(terms: Set<string>, fullName: string): void {
    const nameParts = fullName.split(/\s+/).filter(Boolean);
    if (nameParts.length === 0) {
        return;
    }

    addTerm(terms, nameParts[0]);

    if (nameParts.length > 1) {
        addTerm(terms, nameParts[nameParts.length - 1]);
    }
}

/**
 * Builds the list of user-specific strings to redact from analytics payloads.
 *
 * Terms are derived from the signed-in user's claims and returned longest-first so
 * full-name matches are replaced before shorter overlapping tokens (e.g. "John Doe"
 * before "John").
 *
 * Name terms come from `userFullName` claims and are extracted as follows:
 * - the full trimmed name
 * - the first name (first whitespace-separated token)
 * - the last name (last token, when the name has more than one token)
 *
 * Username terms come from `sub`, which is expected to be JSON containing `uname`.
 * The domain prefix is stripped before adding the term (e.g. `CSCINFO\\johndoe`
 * -> `johndoe`). Invalid or missing `sub` JSON is ignored; name terms still apply.
 *
 * @returns Deduplicated redaction terms, or an empty array when claims are missing.
 */
export function getUserRedactionTermsFromClaims(claims: UserClaims | undefined | null): string[] {
    const terms = new Set<string>();

    const fullName = claims?.userFullName?.trim();
    if (fullName) {
        addTerm(terms, fullName);
        addFirstAndLastNameTerms(terms, fullName);
    }

    const sub = claims?.sub;
    if (sub) {
        try {
            const parsedSub = JSON.parse(sub) as { uname?: unknown };
            if (typeof parsedSub.uname === 'string') {
                const username = extractUsernameFromUname(parsedSub.uname);
                addTerm(terms, username);
            }
        } catch {
            // Ignore invalid sub JSON; userFullName terms still apply.
        }
    }

    return [...terms].sort((a, b) => b.length - a.length);
}

/**
 * Replaces every case-insensitive occurrence of a redaction term in `text`.
 *
 * Each term is matched literally (regex metacharacters are escaped) and replaced
 * with {@link ANALYTICS_REDACTED_PLACEHOLDER}. Terms are applied in array order,
 * so callers should pass longer terms first when overlaps are possible.
 *
 * @returns The original `text` when it is empty or `redactionTerms` is empty.
 */
export function redactTermsInText(text: string, redactionTerms: readonly string[]): string {
    if (!text || redactionTerms.length === 0) {
        return text;
    }

    let result = text;
    for (const term of redactionTerms) {
        if (!term) {
            continue;
        }

        const regex = new RegExp(escapeRegExp(term), 'gi');
        result = result.replace(regex, ANALYTICS_REDACTED_PLACEHOLDER);
    }

    return result;
}

/**
 * Applies {@link redactTermsInText} to every string leaf in a deep copy of `record`.
 *
 * Walks nested plain objects and arrays the same way URL sanitization does.
 * Non-string primitives are left unchanged. When `redactionTerms` is empty,
 * the original record reference is returned as-is.
 */
export function redactTermsInRecord(record: Record<string, unknown>, redactionTerms: readonly string[]): Record<string, unknown> {
    if (redactionTerms.length === 0) {
        return record;
    }

    return redactValue(record, redactionTerms) as Record<string, unknown>;
}

function redactValue(value: unknown, redactionTerms: readonly string[]): unknown {
    if (typeof value === 'string') {
        return redactTermsInText(value, redactionTerms);
    }
    if (Array.isArray(value)) {
        return value.map((item) => redactValue(item, redactionTerms));
    }
    if (value && typeof value === 'object') {
        const result: Record<string, unknown> = {};
        for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
            result[key] = redactValue(nested, redactionTerms);
        }
        return result;
    }
    return value;
}
